import { z } from 'zod';

/**
 * Paginación de los listados del padrón.
 *
 * Por desplazamiento y no por cursor: un padrón son cientos de unidades, no
 * millones, y el administrador quiere saltar a la página 7 de la tabla. El
 * cursor se gana su lugar en la bitácora de accesos, que sí crece sin techo.
 */
export const paginacionSchema = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  /** Techo de 200: evita que un `?porPagina=100000` tumbe el servidor. */
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
  buscar: z.string().trim().max(100).optional(),
});
export type Paginacion = z.infer<typeof paginacionSchema>;

export interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  porPagina: number;
  paginas: number;
}

export function armarPagina<T>(datos: T[], total: number, p: Paginacion): Pagina<T> {
  return {
    datos,
    total,
    pagina: p.pagina,
    porPagina: p.porPagina,
    paginas: Math.max(1, Math.ceil(total / p.porPagina)),
  };
}

/**
 * Por qué columna se ordena una lista, y en qué dirección.
 *
 * El nombre del campo **no** se pasa a la consulta tal cual: cada pantalla
 * traduce lo permitido a su `orderBy`. Poner aquí el nombre de una columna de la
 * base dejaría que cualquiera ordenara por `passwordHash` y averiguara cosas de
 * los datos sin llegar a leerlos.
 */
export const ordenSchema = z.object({
  orden: z.string().trim().max(40).optional(),
  dir: z.enum(['asc', 'desc']).default('asc'),
});
export type Orden = z.infer<typeof ordenSchema>;

export const listaSchema = paginacionSchema.merge(ordenSchema);
export type Lista = z.infer<typeof listaSchema>;

/**
 * Rango de fechas opcional para filtrar un historial. Las dos puntas son
 * opcionales: el residente puede acotar solo el inicio, solo el fin, o ninguno.
 */
export const rangoFechasSchema = z.object({
  desde: z.string().datetime().optional(),
  hasta: z.string().datetime().optional(),
});
export type RangoFechas = z.infer<typeof rangoFechasSchema>;

/** Paginación + rango de fechas: lo que reciben los historiales del residente. */
export const historialSchema = paginacionSchema.merge(rangoFechasSchema);
export type Historial = z.infer<typeof historialSchema>;

/** Construye el filtro Prisma de un rango sobre un campo de fecha, o `{}`. */
export function filtroRango(campo: string, r: RangoFechas): Record<string, unknown> {
  if (!r.desde && !r.hasta) return {};
  return {
    [campo]: {
      ...(r.desde ? { gte: new Date(r.desde) } : {}),
      ...(r.hasta ? { lte: new Date(r.hasta) } : {}),
    },
  };
}

/** Identificador de Prisma. Se valida para que un id inventado dé 400 y no 500. */
export const idSchema = z.string().cuid();

/**
 * Los listados incluyen inactivos solo si se piden. Dar de baja en Kodica es
 * desactivar, nunca borrar: la unidad de un residente que se fue sigue
 * colgando de sus accesos históricos.
 */
export const filtroActivosSchema = z.object({
  incluirInactivos: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .default(false),
});

/**
 * Filtros del historial del portal.
 *
 * Paginación y rango de fechas, más lo que de verdad se pregunta al mirar hacia
 * atrás: por qué vivienda, de dónde vino y cómo acabó. Cada pestaña usa los que
 * le aplican y de ahí que todos sean opcionales.
 */
export const filtroHistorialSchema = historialSchema.extend({
  unidadId: z.string().cuid().optional(),
  puntoAccesoId: z.string().cuid().optional(),
  origen: z.string().trim().max(20).optional(),
  estado: z.string().trim().max(30).optional(),
  /** `true` deja solo lo que salió mal, que es lo que se busca en una queja. */
  soloFallidas: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .default(false),
});
export type FiltroHistorial = z.infer<typeof filtroHistorialSchema>;
